/**
 * C.9 — Save-provider abstraction for the Obsidian Vault.
 *
 * The editor's local-edit buffer is flushed through a `VaultSaveProvider`
 * which encapsulates:
 *   - debounced auto-save semantics
 *   - eTag-gated round-trip to wherever the vault lives (SharePoint /
 *     Convex-only / local in-memory)
 *   - 412 conflict surfacing
 *   - session-start checkpoint + per-save version persistence (Q29/Q34)
 *
 * Two concrete providers ship in C.9:
 *   - `createInMemorySaveProvider` — used by the sample-vault demo
 *     desktop. Stores edits in-memory + writes to the paired demo
 *     Convex tables for audit.
 *   - `createSharePointSaveProvider` — production mode. Calls the
 *     Graph adapter with `If-Match: <etag>`. Full Graph wiring runs
 *     through `lib/graph/sharepoint.ts`'s existing helpers.
 *
 * The provider deliberately stays React-free so it can be exercised in
 * vitest without RTL.
 *
 * @see {@link ../writes/lanes.md} — this module is **lane B** in the
 * writes-routing vocabulary. Other lanes: A (`edits/localEditQueue.ts`),
 * C (`nipe/pendingWriteBodies.ts`).
 */

import type { ParsedNote } from './parse';

export type SaveStatus =
  | 'idle'
  | 'pending'
  | 'saving'
  | 'saved'
  | 'conflict'
  | 'error';

export interface SaveResult {
  status: 'saved';
  newEtag: string;
  savedAt: number;
}

export interface ConflictResult {
  status: 'conflict';
  /** ETag observed on the server right before the PUT. */
  serverEtag: string;
  /** Server's current body (for the merge dialog). */
  serverBody: string;
}

export interface SaveError {
  status: 'error';
  message: string;
}

export type FlushOutcome = SaveResult | ConflictResult | SaveError;

export interface SaveProviderState {
  /** Map of notePath → in-flight or pending markdown. */
  pendingByPath: Record<string, string>;
  /** Last known status per note. */
  statusByPath: Record<string, SaveStatus>;
  /** Last error per note (cleared on next successful save). */
  errorByPath: Record<string, string>;
}

export interface VaultSaveProvider {
  /** Queue a markdown body for `notePath`. Triggers debounced flush. */
  queueEdit(notePath: string, markdown: string): void;
  /** Force-flush any pending edits immediately. Resolves with results. */
  flushAll(): Promise<Record<string, FlushOutcome>>;
  /** Snapshot all open notes on session start so user can revert. */
  takeSessionCheckpoint(notes: ParsedNote[]): Promise<void>;
  /** Discard a note's pending edit (e.g., user clicks "Revert"). */
  discard(notePath: string): void;
  /** Subscribe to state changes. Returns an unsubscribe fn. */
  subscribe(listener: (state: SaveProviderState) => void): () => void;
  /** Current state snapshot. */
  getState(): SaveProviderState;
  /** Stop debounce timers (e.g., on unmount). */
  dispose(): void;
}

export interface PutOptions {
  notePath: string;
  markdown: string;
  /** Expected current eTag. The transport sends `If-Match: <etag>`. */
  expectedEtag: string | null;
}

export interface PutTransport {
  /** Returns `'saved'` with new etag, `'conflict'` with server state, or `'error'`. */
  put(options: PutOptions): Promise<FlushOutcome>;
  /** Reads the current server-side body + etag for the conflict resolver. */
  fetchCurrent(notePath: string): Promise<{ body: string; etag: string } | null>;
}

export interface CheckpointWriter {
  /** Persist a single note's checkpoint for this session. */
  writeCheckpoint(notePath: string, body: string, etag: string): Promise<void>;
  /** Persist a per-save version row. */
  writeVersion(notePath: string, body: string, etag: string): Promise<void>;
}

export interface SaveProviderConfig {
  /** Debounce window for auto-save (ms). 0 = save immediately. */
  debounceMs?: number;
  /** Initial eTags so we can `If-Match` correctly on first save. */
  initialEtags: Record<string, string>;
  transport: PutTransport;
  checkpointWriter?: CheckpointWriter;
  /** Stable session id (Q34 — clientCheckpoint key). */
  sessionId: string;
  /** Called whenever state changes; in addition to subscribe listeners. */
  onStateChange?: (state: SaveProviderState) => void;
}

const DEFAULT_DEBOUNCE_MS = 2000;

export function createSaveProvider(config: SaveProviderConfig): VaultSaveProvider {
  const debounceMs = config.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const etagByPath: Record<string, string> = { ...config.initialEtags };
  let state: SaveProviderState = {
    pendingByPath: {},
    statusByPath: {},
    errorByPath: {},
  };
  const listeners = new Set<(state: SaveProviderState) => void>();
  const flushTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  let disposed = false;

  function notify() {
    config.onStateChange?.(state);
    for (const l of listeners) l(state);
  }

  function patch(next: Partial<SaveProviderState>) {
    state = {
      pendingByPath: { ...state.pendingByPath, ...(next.pendingByPath ?? {}) },
      statusByPath: { ...state.statusByPath, ...(next.statusByPath ?? {}) },
      errorByPath: { ...state.errorByPath, ...(next.errorByPath ?? {}) },
    };
    notify();
  }

  function setStatus(notePath: string, status: SaveStatus, error?: string) {
    const nextStatus = { ...state.statusByPath, [notePath]: status };
    const nextErrors = { ...state.errorByPath };
    if (error) nextErrors[notePath] = error;
    else delete nextErrors[notePath];
    state = {
      pendingByPath: state.pendingByPath,
      statusByPath: nextStatus,
      errorByPath: nextErrors,
    };
    notify();
  }

  async function flushOne(notePath: string): Promise<FlushOutcome> {
    const markdown = state.pendingByPath[notePath];
    if (typeof markdown !== 'string') {
      return { status: 'error', message: 'no pending edit' };
    }
    setStatus(notePath, 'saving');
    try {
      const outcome = await config.transport.put({
        notePath,
        markdown,
        expectedEtag: etagByPath[notePath] ?? null,
      });
      if (outcome.status === 'saved') {
        etagByPath[notePath] = outcome.newEtag;
        // Pull the path out of pending; mark saved.
        const nextPending = { ...state.pendingByPath };
        delete nextPending[notePath];
        state = {
          pendingByPath: nextPending,
          statusByPath: { ...state.statusByPath, [notePath]: 'saved' },
          errorByPath: { ...state.errorByPath, [notePath]: '' },
        };
        notify();
        if (config.checkpointWriter) {
          await config.checkpointWriter.writeVersion(
            notePath,
            markdown,
            outcome.newEtag,
          );
        }
      } else if (outcome.status === 'conflict') {
        setStatus(
          notePath,
          'conflict',
          `SharePoint version is newer (etag ${outcome.serverEtag.slice(0, 8)}…)`,
        );
      } else {
        setStatus(notePath, 'error', outcome.message);
      }
      return outcome;
    } catch (err) {
      const msg = (err as Error).message ?? String(err);
      setStatus(notePath, 'error', msg);
      return { status: 'error', message: msg };
    }
  }

  function scheduleFlush(notePath: string) {
    if (flushTimers[notePath]) clearTimeout(flushTimers[notePath]);
    if (debounceMs === 0) {
      void flushOne(notePath);
      return;
    }
    flushTimers[notePath] = setTimeout(() => {
      delete flushTimers[notePath];
      if (disposed) return;
      void flushOne(notePath);
    }, debounceMs);
  }

  return {
    queueEdit(notePath, markdown) {
      patch({
        pendingByPath: { [notePath]: markdown },
        statusByPath: { [notePath]: 'pending' },
      });
      scheduleFlush(notePath);
    },
    async flushAll() {
      const paths = Object.keys(state.pendingByPath);
      const results: Record<string, FlushOutcome> = {};
      for (const p of paths) {
        if (flushTimers[p]) {
          clearTimeout(flushTimers[p]);
          delete flushTimers[p];
        }
        results[p] = await flushOne(p);
      }
      return results;
    },
    async takeSessionCheckpoint(notes) {
      if (!config.checkpointWriter) return;
      // Run sequentially to keep the demo Convex audit log readable.
      // For 100s of notes this should be batched (FT-13).
      for (const n of notes) {
        const etag = etagByPath[n.path] ?? '';
        await config.checkpointWriter.writeCheckpoint(n.path, n.body, etag);
      }
    },
    discard(notePath) {
      if (flushTimers[notePath]) {
        clearTimeout(flushTimers[notePath]);
        delete flushTimers[notePath];
      }
      const nextPending = { ...state.pendingByPath };
      delete nextPending[notePath];
      const nextStatus = { ...state.statusByPath };
      delete nextStatus[notePath];
      const nextErrors = { ...state.errorByPath };
      delete nextErrors[notePath];
      state = {
        pendingByPath: nextPending,
        statusByPath: nextStatus,
        errorByPath: nextErrors,
      };
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getState() {
      return state;
    },
    dispose() {
      disposed = true;
      for (const t of Object.values(flushTimers)) clearTimeout(t);
      listeners.clear();
    },
  };
}

// ---------------------------------------------------------------------------
// In-memory transport — used by the demo desktop.
// ---------------------------------------------------------------------------

/**
 * Backing store for the in-memory transport. Lives in the closure of
 * `createInMemoryTransport` so multiple notes share one map. The "etag"
 * is just `etag-<counter>` so conflict tests can simulate divergence.
 */
export interface InMemoryStore {
  put(path: string, body: string, expectedEtag: string | null): FlushOutcome;
  /** Externally mutate a note's body (simulates "another user edited"). */
  external(path: string, body: string): void;
  read(path: string): { body: string; etag: string } | null;
}

export function createInMemoryStore(
  seed: { path: string; body: string }[],
): InMemoryStore {
  const data = new Map<string, { body: string; etag: string }>();
  let counter = 0;
  function nextEtag() {
    counter += 1;
    return `etag-${counter}`;
  }
  for (const row of seed) {
    data.set(row.path, { body: row.body, etag: nextEtag() });
  }
  return {
    put(path, body, expectedEtag) {
      const current = data.get(path);
      if (current && expectedEtag !== null && current.etag !== expectedEtag) {
        return {
          status: 'conflict',
          serverEtag: current.etag,
          serverBody: current.body,
        };
      }
      const newEtag = nextEtag();
      data.set(path, { body, etag: newEtag });
      return { status: 'saved', newEtag, savedAt: Date.now() };
    },
    external(path, body) {
      data.set(path, { body, etag: nextEtag() });
    },
    read(path) {
      return data.get(path) ?? null;
    },
  };
}

export function createInMemoryTransport(store: InMemoryStore): PutTransport {
  return {
    async put(options) {
      return store.put(options.notePath, options.markdown, options.expectedEtag);
    },
    async fetchCurrent(notePath) {
      return store.read(notePath);
    },
  };
}
